import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { dealsAPI } from '@/lib/api';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const NAVY = '#0A1F44';
const TEAL = '#0E7F8E';
const MED_GRAY = '#6B7280';

export default function LenderPackagePreview({ open, onClose, dealId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !dealId) return;
    setLoading(true);
    dealsAPI.lenderPackagePreview(dealId)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load lender package preview'))
      .finally(() => setLoading(false));
  }, [open, dealId]);

  const handleDownload = () => {
    const url = dealsAPI.lenderPackageDownloadUrl(dealId);
    const token = localStorage.getItem('cts_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Lender_Package_${dealId}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => toast.error('Failed to download package'));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="bg-white border-gray-200 text-gray-900 max-w-4xl max-h-[92vh] overflow-y-auto p-0"
        data-testid="lender-package-preview-dialog"
      >
        <DialogHeader className="p-4 border-b border-gray-200 flex flex-row items-center justify-between">
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            Lender Package Preview
          </DialogTitle>
          <Button
            size="sm"
            className="bg-[#E31937] hover:bg-[#C4122C] text-white mr-8"
            onClick={handleDownload}
            data-testid="lender-pkg-download-btn"
          >
            <Download className="w-4 h-4 mr-1" /> Download PDF
          </Button>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}

        {data && !loading && (
          <Page1Preview data={data} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Page1Preview({ data }) {
  const branding = data.branding || {};
  const contact = branding.contact || {};
  const logoSrc = branding.primary_logo
    ? `${process.env.REACT_APP_BACKEND_URL}${branding.primary_logo}`
    : null;

  return (
    <div
      className="mx-auto bg-white"
      style={{
        width: '720px',
        padding: '38px',
        color: NAVY,
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: '11px',
        lineHeight: 1.5,
      }}
      data-testid="lender-pkg-page1"
    >
      {/* HEADER */}
      <div className="flex items-start justify-between" style={{ minHeight: 70 }}>
        <div className="flex-1">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt="Logo"
              style={{ maxHeight: 70, maxWidth: 200, objectFit: 'contain' }}
              data-testid="lender-pkg-logo"
            />
          ) : (
            <div style={{ fontWeight: 700, fontSize: 15, color: NAVY, textTransform: 'uppercase' }}>
              {branding.company_name || 'Canadian Truck Sales'}
            </div>
          )}
        </div>
        <div className="flex" style={{ paddingLeft: 16 }}>
          <div style={{ borderLeft: '0.5px solid #D1D5DB', paddingLeft: 16, fontSize: 10, color: NAVY }}>
            {contact.address && <div style={{ marginBottom: 4 }}>{contact.address}</div>}
            {contact.phone && <div style={{ marginBottom: 4 }}>{contact.phone}</div>}
            {contact.email && <div style={{ marginBottom: 4 }}>{contact.email}</div>}
            {contact.website && <div>{contact.website}</div>}
          </div>
        </div>
      </div>

      <hr style={{ border: 0, borderTop: `1.4px solid ${NAVY}`, margin: '14px 0 0 0' }} />

      {/* TITLE */}
      <div className="text-center" style={{ marginTop: 32, marginBottom: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: NAVY, letterSpacing: '0.5px' }}>
          TRANSACTION SUMMARY
        </div>
        <div style={{ fontSize: 11, color: MED_GRAY, marginTop: 6 }}>
          Commercial Equipment Finance Request
        </div>
        <div style={{ fontSize: 10, color: MED_GRAY, marginTop: 4 }}>{data.date}</div>
      </div>

      {/* SECTION 1: BORROWER */}
      <SectionHeader>1. BORROWER / LESSEE</SectionHeader>
      <div style={{ marginBottom: 20 }}>
        <BorrowerRow label="Legal Name:" value={data.borrower.legal_name} />
        <BorrowerRow label="Address:" value={data.borrower.address} />
        <BorrowerRow label="Contact:" value={data.borrower.contact} />
        <BorrowerRow label="Phone:" value={data.borrower.phone} />
        <BorrowerRow label="Email:" value={data.borrower.email} />
        <BorrowerRow label="Industry:" value={data.borrower.industry} />
      </div>

      {/* SECTION 2: TRANSACTION REQUEST */}
      <SectionHeader>2. TRANSACTION REQUEST</SectionHeader>
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '0.6px solid #E5E7EB', marginBottom: 20 }}>
        <tbody>
          <KVRow label="Requested Amount:" value={data.transaction.requested_amount} />
          <KVRow label="Equipment:" value={data.transaction.equipment} />
          <KVRow label="Supplier / Dealer:" value={data.transaction.supplier} />
          <KVRow label="Term:" value={data.transaction.term} />
          <KVRow label="Payment / Down Payment:" value={data.transaction.payment} />
        </tbody>
      </table>

      {/* SECTION 3: SUPPORTING DOCUMENTS */}
      <SectionHeader>3. SUPPORTING DOCUMENTS</SectionHeader>
      <div
        style={{
          border: '0.6px solid #E5E7EB',
          padding: '12px 16px',
          marginBottom: 20,
          fontSize: 10.5,
        }}
      >
        {data.supporting_docs && data.supporting_docs.length > 0 ? (
          <>
            {data.supporting_docs.map((d, i) => (
              <div key={`${d.filename}-${i}`} style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 700, color: NAVY }}>• {d.label}</div>
                <div style={{ color: MED_GRAY, marginLeft: 12, fontSize: 10 }}>{d.filename}</div>
              </div>
            ))}
            <div style={{ fontWeight: 700, color: NAVY, marginTop: 6 }}>
              Total: {data.supporting_docs.length} document(s)
            </div>
          </>
        ) : (
          <div style={{ color: MED_GRAY, fontStyle: 'italic' }}>
            No supporting documents uploaded.
          </div>
        )}
      </div>

      {/* SECTION 4: ITEMS TO CONFIRM */}
      <SectionHeader>4. ITEMS TO CONFIRM</SectionHeader>
      <div
        style={{
          border: '0.6px solid #F59E0B',
          background: '#FEF7E0',
          padding: '12px 16px',
          fontSize: 10.5,
          color: NAVY,
        }}
      >
        {data.missing && data.missing.length > 0 ? (
          <>
            <div style={{ marginBottom: 8 }}>
              The following items should be confirmed before final underwriting review:
            </div>
            {data.missing.map((item) => (
              <div key={item} style={{ marginLeft: 12, marginTop: 2 }}>• {item}</div>
            ))}
          </>
        ) : (
          <div>Transaction summary appears complete for lender review.</div>
        )}
      </div>

      {/* NO FOOTER per spec */}
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TEAL,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          paddingBottom: 4,
          borderBottom: `0.8px solid ${TEAL}`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function BorrowerRow({ label, value }) {
  return (
    <div style={{ display: 'flex', marginBottom: 6, fontSize: 10.5 }}>
      <div style={{ width: 95, fontWeight: 700, color: NAVY, flexShrink: 0 }}>{label}</div>
      <div style={{ color: NAVY, flex: 1 }}>{value || ''}</div>
    </div>
  );
}

function KVRow({ label, value }) {
  return (
    <tr style={{ borderBottom: '0.4px solid #E5E7EB' }}>
      <td
        style={{
          width: 165,
          padding: '10px 10px',
          fontWeight: 700,
          color: NAVY,
          borderRight: '0.4px solid #E5E7EB',
          fontSize: 10.5,
        }}
      >
        {label}
      </td>
      <td style={{ padding: '10px 10px', color: NAVY, fontSize: 10.5 }}>{value}</td>
    </tr>
  );
}
